import React from 'react'

import { cva, type VariantProps } from 'class-variance-authority'

import { AnimatedBox } from '@/components/atoms/AnimatedBox/AnimatedBox.tsx'
import { AutomatedStatus, type AutomatedStatusProps } from '@/components/atoms/AutomatedStatus/AutomatedStatus.tsx'
import { Icons } from '@/components/atoms/Icons/Icons.tsx'
import { Status, type StatusData, type StatusState, type TimeAgoFormat } from '@/components/molecules/Status/Status.tsx'
import { cn } from '@/lib/utils.ts'

const cardVariants = cva(
  'inline-flex w-full flex-col items-start gap-2 border-b border-gray-300 py-3.5 text-base text-gray-800',
  {
    variants: {
      state: {
        default: 'cursor-pointer hover:bg-gray-200',
        active: 'bg-blue-200',
        selected: 'border border-dashed border-gray-900 bg-orange-200',
        disabled: 'opacity-20',
      },
      size: {
        default: 'px-0',
        sm: 'px-8',
      },
    },
    defaultVariants: {
      state: 'default',
      size: 'default',
    },
  }
)

export type CardTimeFormat = TimeAgoFormat

export interface CardLabels {
  distanceTime: string
}

export interface RequestCardData {
  piloteId: string
  piloteIdUrl: string
  fullName: string
  phone: string
  addressFrom: string
  addressFromName?: string
  addressTo: string
  detail: string
  distanceTime: string
  date: string
  automatedStatus?: AutomatedStatusProps['variant']
  isMobileRequest: boolean
  internalNote: string
  cancellationReason?: string
}

export interface CardState extends StatusState {}

export interface CardData extends Pick<StatusData, 'team' | 'donation'> {
  request: RequestCardData
  requestStatus: StatusData['status']
  locationLabel: string
  status: Pick<StatusData, 'description' | 'since' | 'willExecuteAt'>
  hidden?: boolean
}

export interface CardProps
  extends
    React.HtmlHTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants>,
    CardState,
    CardData,
    CardTimeFormat {
  labels: CardLabels
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className,
      children,
      state,
      size,
      labels,
      request,
      requestStatus,
      locationLabel,
      team,
      status,
      hidden = false,
      donation,
      selectedTeam,
      timeAgoFormatter,
      willExecuteInTimeAgoFormatter,
      ...props
    },
    ref
  ) => (
    <AnimatedBox>
      {hidden ? null : (
        <div className={cn(cardVariants({ state, size, className }))} ref={ref} {...props}>
          {size === 'sm' ? (
            <div className="flex justify-end">
              <Status
                status={requestStatus}
                description={status.description}
                since={status.since}
                team={team}
                pin={{ letter: locationLabel }}
                donation={donation}
                willExecuteAt={status.willExecuteAt}
                selectedTeam={selectedTeam}
                timeAgoFormatter={timeAgoFormatter}
                willExecuteInTimeAgoFormatter={willExecuteInTimeAgoFormatter}
              />
            </div>
          ) : (
            <>
              <div className="grid w-full grid-cols-12 gap-1 px-8">
                <div className="col-span-2">
                  <div className="flex flex-col items-start gap-1">
                    <div className="flex items-center gap-0.5 text-blue-700 uppercase select-none">
                      {request.automatedStatus && <AutomatedStatus variant={request.automatedStatus} />}
                      <span>#</span>
                      <a href={request.piloteIdUrl} target="_blank" className="underline" rel="noreferrer">
                        {request.piloteId}
                      </a>
                      {request.isMobileRequest && <Icons.mobileRequest className="size-3.5 fill-blue-600 pl-0.5" />}
                    </div>
                    <p className="font-bold">{request.fullName}</p>
                    <p>{request.phone}</p>
                  </div>
                </div>
                <div className="col-span-5">
                  <div className="flex flex-col items-start gap-2">
                    <div className="flex items-start gap-1 self-stretch">
                      <Icons.pinSmall className="size-4 fill-blue-500" />
                      <p className="flex-[1_0_0]">
                        {request.addressFromName && <span className="font-bold">{request.addressFromName} - </span>}
                        <span>{request.addressFrom}</span>
                      </p>
                    </div>
                    <div className="flex items-start gap-1 self-stretch">
                      <Icons.flagSmall className="size-4 fill-green-500" />
                      <p className="flex-[1_0_0]">{request.addressTo}</p>
                    </div>
                    {request.cancellationReason && (
                      <div className="flex items-start gap-1 self-stretch">
                        <div className="relative flex size-4 shrink-0 items-center justify-center">
                          <Icons.x className="z-10 scale-66 stroke-red-800" />
                          <Icons.circle className="absolute top-0 left-0 fill-red-500 stroke-red-800" />
                        </div>
                        <p className="flex-[1_0_0] text-red-700">{request.cancellationReason}</p>
                      </div>
                    )}
                    {request.internalNote && (
                      <div className="flex items-start gap-1 self-stretch">
                        <Icons.chat className="size-4 fill-yellow-400" />
                        <p className="flex-[1_0_0] truncate text-yellow-600 italic">{request.internalNote}</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-span-3 select-none">
                  <div className="flex flex-col items-start gap-1">
                    <p className="self-stretch">{request.detail}</p>
                    <p className="self-stretch leading-5">
                      <span>{labels.distanceTime}</span>
                      <span className="font-bold">{request.distanceTime}</span>
                    </p>
                    <p className="self-stretch">{request.date}</p>
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="flex justify-end">
                    <Status
                      className={cn(
                        (requestStatus === 'completed' || requestStatus === 'cancelled') && 'min-w-[120px] text-left'
                      )}
                      status={requestStatus}
                      description={status.description}
                      since={status.since}
                      team={team}
                      pin={{ letter: locationLabel }}
                      donation={donation}
                      willExecuteAt={status.willExecuteAt}
                      selectedTeam={selectedTeam}
                      timeAgoFormatter={timeAgoFormatter}
                      willExecuteInTimeAgoFormatter={willExecuteInTimeAgoFormatter}
                    />
                  </div>
                </div>
              </div>
              {(state === 'active' || state === 'selected') && (
                <div className="flex h-[29px] w-full items-center justify-between self-stretch px-8 py-0">
                  {children}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </AnimatedBox>
  )
)
